import React, { useState, useRef, useEffect } from "react";
import { useDraggable } from "@/hooks/useDraggable";
import { tr } from '@/i18n/tr';
import axios, { API } from "@/lib/api";
import { Crown, X, Send, Loader2 } from "lucide-react";

/** Super Admin AI chat widget — cross-tenant control via natural language. */
export default function SuperAIWidget() {
  const [open, setOpen] = useState(false);
  const drag = useDraggable("super_ai_pos", () => ({ x: window.innerWidth - 80, y: window.innerHeight - 80 }), () => setOpen(true));
  const [messages, setMessages] = useState([
    { role: "assistant", text: tr('x_a75406c6') },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages(p => [...p, { role: "user", text: msg }]);
    setInput(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/super-ai/chat`, { message: msg });
      setMessages(p => [...p, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages(p => [...p, { role: "assistant", text: "⚠️ " + (e.response?.data?.detail || tr('x_720f0600')) }]);
    }
    setLoading(false);
  };

  const suggestions = [tr('x_d21e1fb6'), tr('x_dc45b9fc'), tr('x_1c101627'), tr('x_922f1ff2')];

  return (
    <>
      {!open && (
        <button
          onPointerDown={drag.onPointerDown} style={drag.style}
          className="fixed w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-pink-500 hover:scale-110 transition-transform shadow-xl shadow-amber-900/40 z-50 flex items-center justify-center select-none"
          title="Super Admin AI"
          data-testid="super-ai-trigger"
        >
          <Crown className="w-6 h-6 text-white" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 w-[380px] max-w-[92vw] h-[560px] max-h-[80vh] bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl z-50 flex flex-col" data-testid="super-ai-window">
          <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-gradient-to-r from-amber-500/20 to-pink-500/20 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <div>
                <div className="text-sm font-bold text-white">Super Admin AI</div>
                <div className="text-[10px] text-amber-300">{tr('x_ee98d830')}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="super-ai-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-amber-500 text-white rounded-br-sm" : "bg-slate-800 text-slate-200 rounded-bl-sm"}`}>{m.text}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-slate-800 px-3 py-2 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-amber-400" /></div></div>}
            <div ref={endRef} />
          </div>
          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {suggestions.map(s => (
                <button key={s} onClick={() => setInput(s)} className="text-[11px] px-2 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">{s}</button>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-slate-700 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={tr('x_40e77bcc')} className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50" disabled={loading} data-testid="super-ai-input" />
            <button onClick={send} disabled={loading || !input.trim()} className="bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 w-10 h-10 rounded-xl flex items-center justify-center" data-testid="super-ai-send">
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
